import { useEffect, useState } from "react";
import { Link, useT } from "@/i18n";
import { ArrowRight, CalendarCheck, Mountain, Snowflake, Home as HomeIcon, Users, Sun, Heart, Compass, Trees, Sparkles } from "lucide-react";
import { useSite, useSeo } from "@/context/SiteContext";
import { api, has, pickSeasonalImage, sortBySeason } from "@/lib/api";
import { businessJsonLd } from "@/lib/seo";
import { Section, SectionHead, ApartmentCard, ReviewCard, PoiCard, CtaBanner, Lightbox } from "@/components/public/Shared";

const ICONS = { Mountain, Snowflake, Home: HomeIcon, Users, Sun, Heart, Compass, Trees, Sparkles };

export default function Home() {
  const { content: c, settings, apartments, seo } = useSite();
  const t = useT();
  const [pois, setPois] = useState([]);
  const [media, setMedia] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [lb, setLb] = useState(null);
  useEffect(() => {
    api.get("/public/pois").then((r) => setPois(r.data)).catch(() => {});
    api.get("/public/media").then((r) => setMedia(r.data)).catch(() => {});
    api.get("/public/reviews").then((r) => setReviews(r.data.items)).catch(() => {});
  }, []);
  const homeSeo = seo?.find((p) => p.path === "/");
  useSeo({ jsonLd: businessJsonLd({ settings, content: c, apartments, description: homeSeo?.description }) });

  const hero = c.hero || {}; const w = c.welcome || {}; const q = c.queyras || {};
  const heroImage = pickSeasonalImage(hero);
  const welcomeImage = pickSeasonalImage(w);
  const g = c.group || {};
  const sv = c.sections_visible || {};
  const favorites = pois.filter((p) => p.favorite);
  const nearby = pois.filter((p) => !p.favorite).slice(0, 7);
  const gallery = sortBySeason(media).slice(0, 6);

  return (
    <div data-testid="home-page">
      <section className="relative min-h-[88vh] flex items-center justify-center text-center text-ecru overflow-hidden" data-testid="hero">
        {heroImage && <img src={heroImage} alt="" className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" data-testid="hero-image" />}
        <div className="absolute inset-0 bg-gradient-to-b from-forest-950/50 via-forest-950/30 to-forest-950/70" />
        {has(hero.badge) && <div className="hidden lg:flex absolute right-10 top-16 w-36 h-36 rounded-full bg-forest-800/70 backdrop-blur border border-ecru/20 items-center justify-center text-center p-4"><p className="text-[11px] uppercase tracking-[0.2em] leading-relaxed">{hero.badge.split("·").map((s, i) => <span key={i} className="block">{s.trim()}</span>)}</p></div>}
        <div className="relative container-site py-28 fade-up">
          <h1 className="font-serif uppercase tracking-[0.08em] leading-[1.05] text-4xl sm:text-5xl lg:text-7xl text-ecru">
          <span className="block">Le Chalet Badouille</span>
          <span className="block text-[0.5em] mt-2">Izoard</span>
        </h1>
          {has(hero.subtitle) && <p className="mt-6 text-sm sm:text-base uppercase tracking-[0.28em] text-ecru/90">{hero.subtitle}</p>}
          {has(hero.tagline) && <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.18em] text-ecru/70">{hero.tagline}</p>}
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/appartements" data-testid="hero-explore-apartments-btn" className="btn-light px-8 py-4"><HomeIcon size={18} /> {hero.btn_primary || t("Découvrir nos appartements")} <ArrowRight size={16} /></Link>
            <Link to="/demande-de-disponibilite" data-testid="hero-request-booking-btn" className="btn px-8 py-4 bg-forest/70 backdrop-blur border border-ecru/30 text-ecru hover:bg-forest"><CalendarCheck size={18} /> {hero.btn_secondary || t("Demander une disponibilité")}</Link>
          </div>
        </div>
      </section>

      {sv.reassurance !== false && has(c.reassurance) && (
        <section className="bg-forest text-ecru" data-testid="reassurance">
          <div className="container-site py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {c.reassurance.map((r, i) => { const I = ICONS[r.icon] || Mountain; return (
              <div key={i} className="flex items-center gap-4" data-testid={`reassurance-item-${i}`}>
                <span className="w-12 h-12 rounded-full border border-ecru/25 flex items-center justify-center shrink-0"><I size={20} strokeWidth={1.5} /></span>
                <div><p className="text-sm font-medium">{r.title}</p>{has(r.text) && <p className="text-xs text-ecru/60">{r.text}</p>}</div>
              </div>); })}
          </div>
        </section>
      )}

      {sv.welcome !== false && (
      <Section className="bg-ecru" testId="welcome">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <SectionHead eyebrow={w.eyebrow} title={w.title} />
            {has(w.text) && <p className="mt-6 text-charcoal/75 leading-relaxed whitespace-pre-line">{w.text}</p>}
            {has(c.history?.text) && <p className="mt-4 text-charcoal/75 leading-relaxed whitespace-pre-line">{c.history.text}</p>}
            <Link to="/appartements" className="btn-primary mt-8" data-testid="welcome-cta">{w.btn_label || t("Découvrir nos appartements")} <ArrowRight size={16} /></Link>
          </div>
          <div className="lg:col-span-6 relative">
            {welcomeImage && <img src={welcomeImage} alt={w.title || t("Le chalet")} loading="lazy" className="w-full aspect-[4/3] object-cover rounded-2xl" data-testid="welcome-image" />}
            {has(w.image_caption) && <div className="absolute bottom-4 right-4 bg-ecru/95 rounded-xl px-4 py-3 text-xs"><p className="font-semibold uppercase tracking-wider text-forest">Le Chalet Badouille Izoard</p><p className="text-charcoal/70 mt-0.5">{w.image_caption}</p></div>}
          </div>
        </div>
      </Section>
      )}

      {(sv.apartments !== false || sv.group !== false) && (
      <Section className="bg-cream" testId="home-apartments">
        {sv.apartments !== false && <>
          <SectionHead eyebrow={c.apartments_section?.eyebrow} title={c.apartments_section?.title} align="center" />
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-12">{apartments.map((a) => <ApartmentCard key={a.id} apt={a} compact highlightCapacity />)}</div>
        </>}
        {sv.group !== false && (
        <div className={`rounded-2xl bg-forest text-ecru p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 ${sv.apartments !== false ? "mt-10" : ""}`} data-testid="home-group-banner">
          <span className="w-12 h-12 rounded-full border border-ecru/25 flex items-center justify-center shrink-0"><Users size={20} strokeWidth={1.5} /></span>
          <div className="flex-1"><p className="font-serif text-2xl leading-tight">{g.home_title || t("Séjour en groupe : louez les 3 appartements")}</p>{has(g.home_text) && <p className="text-sm text-ecru/70 mt-1">{g.home_text}</p>}</div>
          <Link to="/sejour-en-groupe" className="btn-light whitespace-nowrap" data-testid="home-group-cta">{g.home_btn || t("Séjour en groupe")} <ArrowRight size={16} /></Link>
        </div>
        )}
      </Section>
      )}

      {sv.queyras !== false && (
      <section className="grid lg:grid-cols-2" data-testid="home-queyras">
        {[["summer", q.summer, "ete", Sun], ["winter", q.winter, "hiver", Snowflake]].map(([k, s, slug, I]) => s && (
          <div key={k} className="relative min-h-[420px] sm:min-h-[520px] flex items-end p-6 sm:p-10 overflow-hidden group" data-testid={`season-${k}`}>
            {s.image_url && <img src={s.image_url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />}
            <div className="absolute inset-0 bg-gradient-to-t from-forest-950/60 to-transparent" />
            <div className="relative bg-ecru/95 rounded-2xl p-6 sm:p-8 max-w-md">
              <p className="eyebrow flex items-center gap-2"><I size={14} /> {s.eyebrow}</p>
              <h3 className="font-serif text-2xl sm:text-3xl text-forest mt-2 leading-tight">{s.title}</h3>
              {has(s.text) && <p className="text-sm text-charcoal/70 mt-3">{s.text}</p>}
              <Link to={`/decouvrir-le-queyras/${slug}`} data-testid={`season-toggle-${k}`} className="btn-primary mt-5 text-xs">{s.btn_label || t("Découvrir")} <ArrowRight size={14} /></Link>
            </div>
          </div>
        ))}
      </section>
      )}

      {sv.pois !== false && nearby.length > 0 && (
        <section className="bg-sage-100 py-12 sm:py-16" data-testid="home-pois">
          <div className="container-site">
            <SectionHead eyebrow={c.poi_section?.eyebrow} title={c.poi_section?.title} align="center" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mt-8 max-w-4xl mx-auto">
              {nearby.slice(0, 4).map((p) => (
                <Link to="/decouvrir-le-queyras/carte" key={p.id} className="group flex flex-col items-center text-center gap-3 p-5 sm:p-6 rounded-2xl bg-white/70 border border-white hover:bg-white hover:shadow-sm transition-all" data-testid={`home-poi-${p.id}`}>
                  <span className="w-14 h-14 rounded-full bg-sage-100 border border-forest/15 flex items-center justify-center text-forest group-hover:scale-105 transition-transform"><Compass size={24} strokeWidth={1.5} /></span>
                  <span className="text-base font-medium text-forest leading-snug">{p.name}</span>
                  {(has(p.distance) || has(p.travel_time)) && <span className="text-xs text-charcoal/60">{[p.distance, p.travel_time].filter(has).join(" · ")}</span>}
                </Link>
              ))}
            </div>
            <div className="text-center mt-8"><Link to="/decouvrir-le-queyras" className="btn-ghost" data-testid="home-queyras-cta">{t("Découvrir le Queyras")} <ArrowRight size={16} /></Link></div>
          </div>
        </section>
      )}

      {sv.addresses !== false && favorites.length > 0 && (
        <Section className="bg-ecru" testId="home-favorites">
          <SectionHead eyebrow={c.addresses_section?.eyebrow} title={c.addresses_section?.title} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">{favorites.slice(0, 3).map((p) => <PoiCard key={p.id} p={p} />)}</div>
        </Section>
      )}

      {sv.gallery !== false && gallery.length > 0 && (
        <Section className="bg-cream" testId="home-gallery">
          <div className="flex items-end justify-between gap-6 flex-wrap"><SectionHead eyebrow={c.gallery_section?.eyebrow} title={c.gallery_section?.title} /><Link to="/galerie" className="btn-ghost" data-testid="home-gallery-cta">{t("Voir la galerie")} <ArrowRight size={16} /></Link></div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mt-10">
            {gallery.map((m, i) => (
              <button key={m.id} onClick={() => setLb(i)} className={`overflow-hidden rounded-2xl bg-sand ${i === 0 ? "col-span-2 row-span-2" : ""}`} aria-label={t("Agrandir : {name}", { name: m.title || m.alt || "" })} data-testid={`home-gallery-item-${i}`}>
                <img src={m.url} alt={m.alt || m.title || ""} loading="lazy" className="w-full h-full object-cover aspect-square hover:scale-[1.03] transition-transform duration-700" />
              </button>
            ))}
          </div>
          <Lightbox items={gallery} index={lb} onClose={() => setLb(null)} onIndex={setLb} />
        </Section>
      )}

      {sv.reviews !== false && reviews.length > 0 && (
        <Section className="bg-ecru" testId="home-reviews">
          <SectionHead eyebrow={c.reviews_section?.eyebrow} title={c.reviews_section?.title} align="center" />
          <div className="grid md:grid-cols-3 gap-6 mt-12">{reviews.slice(0, 3).map((r) => <ReviewCard key={r.id} r={r} apartments={apartments} />)}</div>
          <div className="text-center mt-10"><Link to="/avis-voyageurs" className="btn-ghost">{t("Tous les avis")} <ArrowRight size={16} /></Link></div>
        </Section>
      )}

      {sv.cta !== false && <CtaBanner />}
    </div>
  );
}
